import React from 'react'
import { Table, Tag, Space, Button, Tooltip, Modal } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import {
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import styles from '../Organizations.module.less'
import { getColumnFilter } from '~/Hooks/useTableColumnFilter'
import { UserInviteStatus } from '~/Services/UserService.d'
import { StaffInfo, StaffPayload } from '~/Services/StaffServices'
import { OrgClaim } from '~/Views/Main/Routes'
import { Utils } from '~/Utils'

export const tagColor = (text: string) => {
  let color = 'blue'
  switch (text) {
    case OrgClaim.Member:
      color = 'green'
      break
    case OrgClaim.Superuser:
      color = 'blue'
      break
    case UserInviteStatus.Declined:
    case OrgClaim.Admin:
      color = 'red'
      break
    case UserInviteStatus.Accepted:
      color = 'blue'
      break
    case UserInviteStatus.Pending:
      color = 'orange'
      break
    default:
      color = Utils.stringToColour(text)
      break
  }
  return color
}

interface StaffsTableProps {
  data: StaffInfo[]
  onEdit: (staff: StaffPayload) => void
  onDelete?: (userIds: string[]) => void
  onTableChange?: (pagination: any, filters: any, sorter: any) => void
  columnFilters?: Record<string, any>
  selectedRowKeys?: string[]
  onSelectionChange?: (keys: string[]) => void
}

export const StaffsTable = ({
  data,
  onEdit,
  onDelete,
  onTableChange,
  columnFilters = {},
  selectedRowKeys = [],
  onSelectionChange,
}: StaffsTableProps) => {
  const { t } = useTranslation()

  const handleEdit = (record: StaffPayload) => {
    onEdit(record)
  }

  const handleDelete = (userId: string) => {
    if (onDelete) {
      onDelete([userId])
      if (onSelectionChange) {
        onSelectionChange([])
      }
    }
  }

  const handleBulkDelete = () => {
    if (onDelete && selectedRowKeys.length > 0) {
      onDelete(selectedRowKeys)
      if (onSelectionChange) {
        onSelectionChange([])
      }
    }
  }

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    if (onTableChange) {
      onTableChange(pagination, filters, sorter)
    }
  }

  const columns: ColumnsType<any> = [
    {
      title: t('staff.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 130,
      sorter: true,
      render: (value?: boolean) => {
        const text = value
          ? t('active')
          : t('inactive')

        return <Tag color={value ? 'processing' : 'default'}>{text}</Tag>
      },
    },
    {
      title: t('staff.fullName'),
      key: 'fullName',
      width: 200,
      render: (_, record) =>
        `${record.lastname ?? ''} ${record.firstname ?? ''}`.trim(),
    },
    {
      title: t('staff.roles'),
      dataIndex: 'organizationRoles',
      key: 'organizationRoles',
      width: 180,
      filters: [
        { text: 'Member', value: OrgClaim.Member },
        { text: 'Admin', value: OrgClaim.Admin },
        { text: 'SuperUser', value: OrgClaim.Superuser },
      ],
      render: (roles: string[]) => (
        <>
          {roles?.map((role, idx) => (
            <Tag key={idx} color={tagColor(role)}>
              {role}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: t('Trạng thái mời'),
      dataIndex: 'invitationStatus',
      key: 'invitationStatus',
      width: 180,
      filters: [
        { text: 'Accepted', value: UserInviteStatus.Accepted },
        { text: 'Pending', value: UserInviteStatus.Pending },
        { text: 'Declined', value: UserInviteStatus.Declined },
      ],
      render: (status: string) => {
        let color = 'default'
        switch (status) {
          case UserInviteStatus.Accepted:
            color = 'success'
            break
          case UserInviteStatus.Pending:
            color = 'warning'
            break
          case UserInviteStatus.Declined:
            color = 'error'
            break
        }
        return <Tag color={color}>{status}</Tag>
      },
    },
    {
      title: t('staff.phone'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 150,
      sorter: true,
      ...getColumnFilter('phoneNumber'),
    },
    {
      title: t('staff.email'),
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      ...getColumnFilter('email'),
    },
    {
      title: (
        <Tooltip title={t('actions')}>
          <SettingOutlined />
        </Tooltip>
      ),
      key: 'actions',
      width: 120,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      if (onSelectionChange) {
        onSelectionChange(keys as string[])
      }
    },
  }

  const tableHeight = window.innerHeight - 350

  return (
    <>
      <div className={styles.TableActions}>
        {selectedRowKeys.length > 0 && (
          <>
            <span>
              {t('selected')} <b>{selectedRowKeys.length}</b>
            </span>
            <Button danger icon={<DeleteOutlined />} onClick={handleBulkDelete}>
              {t('remove')}
            </Button>
          </>
        )}
      </div>
      <Table
        className={styles.CustomTableScroll}
        rowKey="id"
        dataSource={data}
        columns={columns}
        onChange={handleTableChange}
        pagination={false}
        bordered
        rowSelection={rowSelection}
        scroll={{ x: 'max-content', y: tableHeight }}
      />
    </>
  )
}
